// Configure tokens in Theme settings -> Cart -> InPost parcel locker / ORLEN Paczka pickup point.
(() => {
    const MODAL_IDS = {
        inpost: 'PopupModal-ParcelLocker',
        orlen: 'PopupModal-OrlenParcelLocker',
    };
    const ATTRIBUTE_ID = 'parcel_locker_id';
    const ATTRIBUTE_LABEL = 'parcel_locker_label';
    const ATTRIBUTE_MODE = 'parcel_locker_mode';
    const ATTRIBUTE_CARRIER = 'parcel_locker_carrier';
    const CARRIERS = {
        inpost: 'inpost',
        orlen: 'orlen',
    };
    const MODES = {
        pickup: 'pickup',
        orlen: 'orlen',
        courier: 'courier',
    };
    // Temporary business pause: InPost remains in the codebase, but the cart defaults to ORLEN.
    const INPOST_DELIVERY_PAUSED = true;
    const SELECTORS = {
        container: '[data-parcel-locker]',
        open: '[data-parcel-locker-open]',
        buttonText: '[data-parcel-locker-button-text]',
        summary: '[data-parcel-locker-summary]',
        actions: '[data-parcel-locker-actions]',
        label: '[data-parcel-locker-label]',
        error: '[data-parcel-locker-error]',
        modeInputs: '[data-parcel-locker-mode-input]',
        checkoutLinks: 'a[href*="/checkout"]',
        checkoutControls: 'button[name="checkout"], input[name="checkout"], [data-cart-standard-checkout]',
    };

    const state = {
        currentId: '',
        currentLabel: '',
        mode: INPOST_DELIVERY_PAUSED ? MODES.orlen : MODES.courier,
        carrier: '',
        cartNote: '',
        cartItemCount: 0,
        settings: null,
        onpointEventName: null,
        orlenDocumentEventBound: false,
        orlenSelectionPollId: null,
        orlenSelectionArmedUntil: 0,
        refreshPromise: null,
        reopenDrawer: null,
        reopenDrawerOpener: null,
        lastContextKey: '',
    };

    const log = (...args) => {
        if (state.settings?.debug) {
            console.log('[ParcelLocker]', ...args);
        }
    };

    const withJsEndpoint = (url, fallback) => {
        const resolved = String(url || '').trim() || fallback;
        if (!resolved) return fallback;
        if (/\.js([?#]|$)/.test(resolved)) return resolved;

        const [path, suffix = ''] = resolved.split(/([?#].*)/, 2);
        return `${path}.js${suffix}`;
    };

    const parseJsonResponse = async (response) => {
        if (!response.ok) {
            throw new Error(`Cart request failed with status ${response.status}`);
        }

        try {
            return await response.json();
        } catch (error) {
            throw new Error('Cart request did not return JSON');
        }
    };

    const getSettings = () => {
        const inpostModal = document.getElementById(MODAL_IDS.inpost);
        const orlenModal = document.getElementById(MODAL_IDS.orlen);
        const inpost = {
            enabled: inpostModal?.dataset.parcelLockerEnabled === 'true',
            token: (inpostModal?.dataset.parcelLockerToken || '').trim(),
            language: inpostModal?.dataset.parcelLockerLanguage || 'pl',
            config: inpostModal?.dataset.parcelLockerConfig || 'parcelCollect',
            debug: inpostModal?.dataset.parcelLockerDebug === 'true',
            modalId: MODAL_IDS.inpost,
        };
        const orlen = {
            enabled: orlenModal?.dataset.parcelLockerEnabled === 'true',
            token: (orlenModal?.dataset.parcelLockerToken || '').trim(),
            debug: orlenModal?.dataset.parcelLockerDebug === 'true',
            modalId: MODAL_IDS.orlen,
        };

        return {
            enabled: inpost.enabled || orlen.enabled,
            debug: inpost.debug || orlen.debug,
            carriers: {
                [CARRIERS.inpost]: inpost,
                [CARRIERS.orlen]: orlen,
            },
        };
    };

    const normalizeCarrier = (value) => {
        const carrier = String(value || '').trim().toLowerCase();
        return carrier === CARRIERS.inpost || carrier === CARRIERS.orlen ? carrier : '';
    };

    const getCarrierForMode = (mode) => {
        if (mode === MODES.courier) return CARRIERS.inpost;
        if (mode === MODES.pickup) return CARRIERS.inpost;
        if (mode === MODES.orlen) return CARRIERS.orlen;
        return '';
    };

    const getDefaultMode = () => (INPOST_DELIVERY_PAUSED ? MODES.orlen : MODES.courier);

    const getAvailableMode = (mode) => {
        if (INPOST_DELIVERY_PAUSED && (mode === MODES.courier || mode === MODES.pickup)) return MODES.orlen;
        return mode || getDefaultMode();
    };

    const getModeForCarrier = (carrier) => {
        if (INPOST_DELIVERY_PAUSED && carrier === CARRIERS.inpost) return '';
        if (carrier === CARRIERS.inpost) return MODES.pickup;
        if (carrier === CARRIERS.orlen) return MODES.orlen;
        return '';
    };

    const isPointMode = (mode) => mode === MODES.pickup || mode === MODES.orlen;

    const getCarrierSettings = (carrier = getCarrierForMode(state.mode) || state.carrier) =>
        state.settings?.carriers?.[carrier] || null;

    const getModalIdForMode = (mode = state.mode) => getCarrierSettings(getCarrierForMode(mode))?.modalId || '';

    const getCartJsonUrl = () => {
        return withJsEndpoint(window.routes?.cart_url, '/cart.js');
    };

    const getCartUpdateUrl = () => withJsEndpoint(window.routes?.cart_update_url, '/cart/update.js');

    const fetchCart = () =>
        fetch(getCartJsonUrl(), {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }).then(parseJsonResponse);

    const normalizeMode = (value) => {
        const mode = String(value || '').trim();
        return mode === MODES.pickup || mode === MODES.orlen || mode === MODES.courier ? mode : '';
    };

    const updateCart = (payload) => {
        const url = getCartUpdateUrl();
        const config =
            typeof fetchConfig === 'function'
                ? fetchConfig()
                : {
                      method: 'POST',
                      headers: {
                          'Content-Type': 'application/json',
                          Accept: 'application/json',
                      },
                  };

        return fetch(url, {
            ...config,
            body: JSON.stringify(payload),
        }).then(parseJsonResponse);
    };

    const getContainers = () => Array.from(document.querySelectorAll(SELECTORS.container));

    const getContextKeyFromElement = (element) =>
        element?.closest?.(SELECTORS.container)?.dataset.parcelLockerContext || '';

    const getCarrierDatasetValue = (container, key, carrier) => {
        if (!container) return '';
        const suffix = carrier === CARRIERS.orlen ? 'Orlen' : 'Inpost';
        const specificKey = `parcelLocker${key}${suffix}`;
        const fallbackKey = `parcelLocker${key}`;
        return container.dataset[specificKey] || container.dataset[fallbackKey] || '';
    };

    const dispatchParcelLockerUpdated = (contextKey = state.lastContextKey) => {
        document.dispatchEvent(
            new CustomEvent('krem:parcel-locker-updated', {
                detail: contextKey ? { context: contextKey } : {},
            })
        );
    };

    const updateUI = () => {
        const containers = getContainers();
        const isPickup = isPointMode(state.mode);
        const carrier = getCarrierForMode(state.mode) || state.carrier;
        const hasSelection = Boolean(state.currentId);
        const labelText = state.currentLabel || state.currentId;

        containers.forEach((container) => {
            const summary = container.querySelector(SELECTORS.summary);
            const actions = container.querySelector(SELECTORS.actions);
            const label = container.querySelector(SELECTORS.label);
            const labelPrefix = container.querySelector('[data-parcel-locker-label-prefix]');
            const error = container.querySelector(SELECTORS.error);
            const openButton = container.querySelector(SELECTORS.open);
            const buttonText = container.querySelector(SELECTORS.buttonText);
            const modeInputs = container.querySelectorAll(SELECTORS.modeInputs);
            const prefix = getCarrierDatasetValue(container, 'SelectedPrefix', carrier);
            const chooseLabel = container.dataset.parcelLockerChoose || '';
            const changeLabel = container.dataset.parcelLockerChange || '';
            const resolvedButtonLabel = isPickup && hasSelection ? changeLabel || chooseLabel : chooseLabel;

            if (summary) summary.hidden = !(isPickup && hasSelection);
            if (actions) actions.hidden = !isPickup;
            if (labelPrefix) {
                labelPrefix.textContent = isPickup && hasSelection ? prefix : '';
            }
            if (label) {
                label.textContent = isPickup && hasSelection ? labelText : '';
            }
            if (buttonText && resolvedButtonLabel) {
                buttonText.textContent = resolvedButtonLabel;
            } else if (openButton && resolvedButtonLabel) {
                openButton.textContent = resolvedButtonLabel;
            }
            modeInputs.forEach((input) => {
                input.checked = input.value === state.mode;
            });
            if (error) error.hidden = true;
        });
    };

    const toggleDynamicCheckoutButtons = (disabled) => {
        document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((element) => {
            element.classList.toggle('cart__dynamic-checkout-buttons--disabled', disabled);
        });
        document.querySelectorAll('.cart-drawer__dynamic-checkout-buttons').forEach((element) => {
            element.classList.toggle('cart-drawer__dynamic-checkout-buttons--disabled', disabled);
        });
    };

    const shouldRequireSelection = () => {
        if (!state.settings?.enabled) return false;
        const hasCartItems =
            state.cartItemCount > 0 ||
            Boolean(document.querySelector('.cart-drawer-item, .cart-item, [id^="CartItem-"]'));
        if (!hasCartItems) return false;
        if (!isPointMode(state.mode)) return false;
        return Boolean(getCarrierSettings()?.enabled);
    };

    const shouldRequireSelectionForCheckout = () => {
        if (!state.settings?.enabled) return false;
        if (!isPointMode(state.mode)) return false;
        return Boolean(getCarrierSettings()?.enabled);
    };

    const updateCheckoutState = () => {
        const shouldBlock = shouldRequireSelection() && !state.currentId;
        toggleDynamicCheckoutButtons(shouldBlock);
    };

    const getContextContainer = (context) => {
        if (!context) return null;
        if (context.id === 'cartDrawerForm') {
            return document.querySelector('cart-drawer')?.querySelector(SELECTORS.container) || null;
        }
        if (context.id === 'cart') {
            return document.querySelector('#MainCartFooter')?.querySelector(SELECTORS.container) || null;
        }
        return context.querySelector?.(SELECTORS.container) || context.closest?.(SELECTORS.container) || null;
    };

    const showError = (message, context) => {
        const contextContainer = getContextContainer(context);
        const containers = contextContainer ? [contextContainer] : getContainers();

        containers.forEach((container) => {
            const error = container.querySelector(SELECTORS.error);
            if (error) {
                if (message) {
                    error.textContent = message;
                }
                error.hidden = false;
            }
        });
    };

    const scrollToWidget = (context) => {
        const contextContainer = getContextContainer(context);
        const container = contextContainer || getContainers()[0];
        if (!container) return;

        container.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });

        const focusTarget = container.querySelector(SELECTORS.open) || container;
        if (focusTarget?.focus) focusTarget.focus();
    };

    const getCheckoutScope = (source) => {
        const element = source instanceof Element ? source : null;
        return (
            element?.closest?.('#MainCartFooter, #CartDrawerSection, #cart, #cartDrawerForm, cart-drawer') ||
            document.querySelector('cart-drawer') ||
            document.querySelector('#MainCartFooter') ||
            document
        );
    };

    const showMissingSelection = (source) => {
        const scope = getCheckoutScope(source);
        const hasVisibleWidget = Boolean(scope?.querySelector?.(SELECTORS.container));
        const drawer = document.querySelector('cart-drawer');

        if (!hasVisibleWidget && drawer?.show) {
            drawer.show(source instanceof Element ? source : undefined);
            window.setTimeout(() => {
                showError(null, drawer);
                scrollToWidget(drawer);
            }, 120);
            return;
        }

        showError(null, scope);
        scrollToWidget(scope);
    };

    const blockCheckoutWithoutSelection = (event, source = event?.target) => {
        if (!shouldRequireSelectionForCheckout() || state.currentId) return false;

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
        }
        showMissingSelection(source);

        return true;
    };

    const refreshFromCart = () => {
        if (state.refreshPromise) return state.refreshPromise;

        state.refreshPromise = fetchCart()
            .then((cart) => {
                const modeValue = normalizeMode(cart.attributes?.[ATTRIBUTE_MODE]);
                const carrierValue = normalizeCarrier(cart.attributes?.[ATTRIBUTE_CARRIER]);
                const hasStoredPoint = Boolean(String(cart.attributes?.[ATTRIBUTE_ID] || '').trim());
                const isStoredOrlenSelection = modeValue === MODES.orlen || carrierValue === CARRIERS.orlen;
                const isPausedInpostSelection =
                    INPOST_DELIVERY_PAUSED &&
                    (modeValue === MODES.courier ||
                        modeValue === MODES.pickup ||
                        carrierValue === CARRIERS.inpost ||
                        (hasStoredPoint && !isStoredOrlenSelection));

                state.currentId = isPausedInpostSelection ? '' : String(cart.attributes?.[ATTRIBUTE_ID] || '').trim();
                state.currentLabel = isPausedInpostSelection
                    ? ''
                    : String(cart.attributes?.[ATTRIBUTE_LABEL] || '').trim();
                state.mode = getAvailableMode(
                    modeValue || (state.currentId ? getModeForCarrier(carrierValue) || MODES.pickup : getDefaultMode())
                );
                state.carrier = getCarrierForMode(state.mode) || carrierValue;
                state.cartItemCount = cart.item_count || 0;
                updateUI();
                updateCheckoutState();
            })
            .catch((error) => {
                log('Failed to refresh cart', error);
            })
            .finally(() => {
                state.refreshPromise = null;
            });

        return state.refreshPromise;
    };

    const parseOrlenPoint = (payload) => {
        const point = payload?.detail?.marker?.options || payload?.detail?.options || payload?.detail || payload;
        if (!point) return null;

        const id =
            point.destinationCode ||
            point.DestinationCode ||
            point.destination_code ||
            point.code ||
            point.Code ||
            point.id ||
            point.Id ||
            point.name ||
            point.Name ||
            point.psd ||
            point.PSD;
        const normalizedId = (id || '').toString().trim();
        if (!normalizedId) return null;

        const explicitLabel = point.label || point.displayName || point.description || point.text;
        const addressLine =
            explicitLabel ||
            point.addressLine ||
            point.AddressLine ||
            point.address ||
            point.Address ||
            point.streetWithNumber ||
            point.street ||
            point.Street ||
            point.streetName;
        const postal = point.postalCode || point.postCode || point.zipCode || point.post_code || point.zip || point.Zip;
        const city = point.city || point.City || point.town || point.place;
        const location = point.location || point.physicalLocationPsd || point.physicalNamePsd;
        const cityLine = [postal, city].filter(Boolean).join(' ');
        const labelParts = [normalizedId, addressLine, cityLine, location].filter(Boolean);

        return {
            id: normalizedId,
            label: labelParts.join(', '),
            carrier: CARRIERS.orlen,
            mode: MODES.orlen,
        };
    };

    const parsePoint = (payload, carrier = CARRIERS.inpost) => {
        if (carrier === CARRIERS.orlen) return parseOrlenPoint(payload);

        const point = payload?.detail || payload?.details || payload;
        if (!point) return null;

        const id =
            point.name || point.id || point.code || point.pointName || point.parcelLockerId || point.parcel_locker_id;
        const address = point.address || point.address_details || point.location || {};
        const parts = [];

        if (typeof address === 'string') {
            parts.push(address);
        } else {
            let street = address.line1;
            if (!street) {
                const streetName = address.street || address.streetName || address.addressLine1;
                const building = address.building_number || address.buildingNumber;
                const flat = address.flat_number || address.flatNumber;
                const streetParts = [streetName, building, flat].filter(Boolean);
                street = streetParts.join(' ');
            }
            const city = address.line2 || address.city || address.town || address.place;
            const postal = address.postCode || address.postalCode || address.zipCode || address.post_code;
            const cityLine = [postal, city].filter(Boolean).join(' ');
            if (street) parts.push(street);
            if (cityLine) parts.push(cityLine);
        }

        const locationDescription = point.locationDescription || point.location_description;
        if (locationDescription) parts.push(locationDescription);

        const normalizedId = (id || '').toString().trim();
        if (!normalizedId) return null;

        const labelParts = [normalizedId, ...parts].filter(Boolean);
        return {
            id: normalizedId,
            label: labelParts.join(', '),
            carrier: CARRIERS.inpost,
            mode: MODES.pickup,
        };
    };

    const saveSelection = async (selection, { contextKey = state.lastContextKey } = {}) => {
        const previous = {
            id: state.currentId,
            label: state.currentLabel,
            mode: state.mode,
            carrier: state.carrier,
        };

        state.lastContextKey = contextKey || state.lastContextKey;
        state.currentId = selection.id;
        state.currentLabel = selection.label;
        state.mode = selection.mode || getModeForCarrier(selection.carrier) || MODES.pickup;
        state.carrier = selection.carrier || getCarrierForMode(state.mode);
        updateUI();
        updateCheckoutState();

        try {
            const cart = await fetchCart();
            const attributes = {
                ...(cart.attributes || {}),
                [ATTRIBUTE_ID]: selection.id,
                [ATTRIBUTE_LABEL]: selection.label,
                [ATTRIBUTE_MODE]: state.mode,
                [ATTRIBUTE_CARRIER]: state.carrier,
            };

            await updateCart({
                attributes,
            });

            updateCheckoutState();
            dispatchParcelLockerUpdated(contextKey);
            return true;
        } catch (error) {
            state.currentId = previous.id;
            state.currentLabel = previous.label;
            state.mode = previous.mode;
            state.carrier = previous.carrier;
            updateUI();
            updateCheckoutState();
            log('Failed to save selection', error);
            showError(window.cartStrings?.error);
            return false;
        }
    };

    const applyMode = (mode, { clearSelection = false } = {}) => {
        state.mode = mode;
        state.carrier = getCarrierForMode(mode);
        if (clearSelection) {
            state.currentId = '';
            state.currentLabel = '';
        }
        updateUI();
        updateCheckoutState();
    };

    const saveMode = async (mode, { clearSelection = false, contextKey = state.lastContextKey } = {}) => {
        const previous = {
            mode: state.mode,
            carrier: state.carrier,
            id: state.currentId,
            label: state.currentLabel,
        };

        state.lastContextKey = contextKey || state.lastContextKey;
        applyMode(mode, { clearSelection });

        try {
            const cart = await fetchCart();
            const attributes = {
                ...(cart.attributes || {}),
                [ATTRIBUTE_MODE]: mode,
                [ATTRIBUTE_CARRIER]: getCarrierForMode(mode),
            };

            if (clearSelection) {
                attributes[ATTRIBUTE_ID] = '';
                attributes[ATTRIBUTE_LABEL] = '';
            }

            await updateCart({
                attributes,
            });

            updateCheckoutState();
            dispatchParcelLockerUpdated(contextKey);
            return true;
        } catch (error) {
            state.mode = previous.mode;
            state.carrier = previous.carrier;
            state.currentId = previous.id;
            state.currentLabel = previous.label;
            updateUI();
            updateCheckoutState();
            log('Failed to save mode', error);
            showError(window.cartStrings?.error);
            return false;
        }
    };

    const handleModeChange = (event) => {
        const source = event?.target && typeof event.target.value !== 'undefined' ? event.target : event?.currentTarget;
        const nextMode = normalizeMode(source?.value) || MODES.courier;
        if (nextMode === state.mode) return;
        const contextKey = getContextKeyFromElement(source);
        const nextCarrier = getCarrierForMode(nextMode);
        const clearSelection = !isPointMode(nextMode) || !isPointMode(state.mode) || nextCarrier !== state.carrier;
        saveMode(nextMode, { clearSelection, contextKey });
    };

    const closeModal = (mode = state.mode) => {
        const modal = document.getElementById(getModalIdForMode(mode));
        stopOrlenHiddenSelectionPolling();
        if (modal?.hide) modal.hide();
    };

    const handlePointSelect = (payload, carrier = CARRIERS.inpost) => {
        log('Point payload', payload);
        const selection = parsePoint(payload, carrier);
        if (!selection) {
            log('Unable to parse selected point', payload);
            return;
        }

        log('Selected point', selection);
        saveSelection(selection, { contextKey: state.lastContextKey }).then((success) => {
            if (success) closeModal(selection.mode);
        });
    };

    const isOrlenSelectionArmed = () => Date.now() <= state.orlenSelectionArmedUntil;

    const armOrlenSelection = () => {
        state.orlenSelectionArmedUntil = Date.now() + 5000;
    };

    const isOrlenChooseControl = (element) => {
        if (!(element instanceof Element)) return false;

        const control = element.closest('button, a, [role="button"], .button, .btn');
        if (!control) return false;

        return /\bwybierz\b/i.test(String(control.textContent || '').trim());
    };

    const handleConfirmedOrlenPointSelect = (payload) => {
        if (!isOrlenSelectionArmed()) {
            log('Ignoring ORLEN point event before confirmation', payload);
            return;
        }

        state.orlenSelectionArmedUntil = 0;
        handlePointSelect(payload, CARRIERS.orlen);
    };

    const bindInpostWidget = () => {
        if (!window.onInpostParcelLockerSelect) {
            window.onInpostParcelLockerSelect = (payload) => handlePointSelect(payload, CARRIERS.inpost);
        }

        const modal = document.getElementById(MODAL_IDS.inpost);
        const widget = modal?.querySelector('inpost-geowidget');
        if (!widget || widget.dataset.parcelLockerBound) return;

        widget.dataset.parcelLockerBound = 'true';

        const onpointEventName = widget.getAttribute('onpoint');
        if (onpointEventName && state.onpointEventName !== onpointEventName) {
            if (state.onpointEventName) {
                document.removeEventListener(state.onpointEventName, window.onInpostParcelLockerSelect);
            }
            document.addEventListener(onpointEventName, window.onInpostParcelLockerSelect);
            state.onpointEventName = onpointEventName;
        }

        ['point', 'pointSelected', 'point:selected'].forEach((eventName) => {
            widget.addEventListener(eventName, window.onInpostParcelLockerSelect);
        });
    };

    const bindOrlenGeneratedWidgets = () => {
        document.querySelectorAll('.orlen-widget-modal').forEach((widget) => {
            if (widget.dataset.parcelLockerOrlenBound) return;
            widget.dataset.parcelLockerOrlenBound = 'true';
            widget.addEventListener('orlenSelectPoint', handleConfirmedOrlenPointSelect);
        });
    };

    const bindOrlenWidget = () => {
        const modal = document.getElementById(MODAL_IDS.orlen);
        const widget = modal?.querySelector('.orlen-widget');
        if (widget && !widget.dataset.parcelLockerBound) {
            widget.dataset.parcelLockerBound = 'true';
            widget.addEventListener('orlenSelectPoint', handleConfirmedOrlenPointSelect);
        }

        if (!state.orlenDocumentEventBound) {
            document.addEventListener(
                'click',
                (event) => {
                    if (!(event.target instanceof Element)) return;
                    if (!event.target.closest(`#${MODAL_IDS.orlen}, .orlen-widget-modal`)) return;
                    if (!isOrlenChooseControl(event.target)) return;

                    armOrlenSelection();
                    window.setTimeout(readOrlenHiddenSelection, 100);
                    window.setTimeout(readOrlenHiddenSelection, 500);
                    window.setTimeout(readOrlenHiddenSelection, 1200);
                },
                true
            );
            document.addEventListener(
                'orlenSelectPoint',
                (event) => {
                    if (!(event.target instanceof Element)) return;
                    if (!event.target.closest(`#${MODAL_IDS.orlen}, .orlen-widget-modal`)) return;
                    handleConfirmedOrlenPointSelect(event);
                },
                true
            );
            state.orlenDocumentEventBound = true;
        }

        bindOrlenGeneratedWidgets();
    };

    const bindWidget = () => {
        bindInpostWidget();
        bindOrlenWidget();
    };

    const hasOpenOrlenPopup = () => {
        const modal = document.getElementById(MODAL_IDS.orlen);
        return Boolean(modal?.querySelector('.leaflet-popup, .leaflet-popup-pane .leaflet-popup-content-wrapper'));
    };

    const getElementText = (element) => String(element?.textContent || '').replace(/\s+/g, ' ').trim();

    const getOrlenViewControl = (root, label) => {
        const selector = 'button, a, [role="button"], [tabindex], div, span';
        const matches = Array.from(root.querySelectorAll(selector)).filter((element) => {
            if (getElementText(element) !== label) return false;
            const childText = Array.from(element.children || [])
                .map(getElementText)
                .filter(Boolean)
                .join(' ');
            return !childText || childText === label;
        });

        return (
            matches.find((element) => element.matches('button, a, [role="button"], [tabindex]')) ||
            matches.find((element) => element.parentElement?.matches('button, a, [role="button"], [tabindex]')) ||
            matches[0] ||
            null
        );
    };

    const getCommonOrlenControlGroup = (root, first, second) => {
        if (!first || !second) return null;

        const firstAncestors = [];
        let node = first;
        while (node && node !== root) {
            firstAncestors.push(node);
            node = node.parentElement;
        }

        node = second;
        while (node && node !== root) {
            if (firstAncestors.includes(node)) return node;
            node = node.parentElement;
        }

        return null;
    };

    const enhanceOrlenWidgetLayout = () => {
        const modal = document.getElementById(MODAL_IDS.orlen);
        const root = modal?.querySelector('.orlen-widget');
        if (!root) return;

        root.classList.add('parcel-locker-orlen-enhanced');
        if (hasOpenOrlenPopup()) return;

        const mapControl = getOrlenViewControl(root, 'Mapa');
        const listControl = getOrlenViewControl(root, 'Lista');
        const switcher = getCommonOrlenControlGroup(root, mapControl, listControl);

        if (switcher) {
            switcher.classList.add('parcel-locker-orlen__view-switcher');
            mapControl?.classList.add('parcel-locker-orlen__view-control');
            listControl?.classList.add('parcel-locker-orlen__view-control');
        }

        const chooseControls = Array.from(root.querySelectorAll('button, a, [role="button"], [tabindex]')).filter(
            (element) => getElementText(element) === 'Wybierz'
        );

        if (chooseControls.length > 1) {
            root.classList.add('parcel-locker-orlen-enhanced--list');
        } else {
            root.classList.remove('parcel-locker-orlen-enhanced--list');
        }
    };

    const refreshOrlenWidgetViewport = ({ resize = false } = {}) => {
        bindOrlenGeneratedWidgets();
        enhanceOrlenWidgetLayout();

        if (resize && !hasOpenOrlenPopup()) {
            window.dispatchEvent(new Event('resize'));
        }
    };

    const scheduleOrlenWidgetEnhancement = () => {
        [
            { delay: 0, resize: false },
            { delay: 120, resize: true },
            { delay: 400, resize: false },
        ].forEach(({ delay, resize }) => {
            window.setTimeout(() => refreshOrlenWidgetViewport({ resize }), delay);
        });
    };

    const readOrlenHiddenSelection = () => {
        const modal = document.getElementById(MODAL_IDS.orlen);
        const target = modal?.querySelector('[data-orlen-parcel-locker-target]');
        const label = modal?.querySelector('[data-orlen-parcel-locker-label]');
        const id = String(target?.value || '').trim();

        if (!isOrlenSelectionArmed()) return false;
        if (!id || id === state.currentId) return false;

        state.orlenSelectionArmedUntil = 0;
        handlePointSelect(
            {
                detail: {
                    destinationCode: id,
                    label: String(label?.value || '').trim(),
                },
            },
            CARRIERS.orlen
        );

        return true;
    };

    const stopOrlenHiddenSelectionPolling = () => {
        if (!state.orlenSelectionPollId) return;
        window.clearInterval(state.orlenSelectionPollId);
        state.orlenSelectionPollId = null;
    };

    const startOrlenHiddenSelectionPolling = () => {
        if (state.mode !== MODES.orlen || state.orlenSelectionPollId) return;

        const startedAt = Date.now();
        state.orlenSelectionPollId = window.setInterval(() => {
            if (readOrlenHiddenSelection() || Date.now() - startedAt > 60000) {
                stopOrlenHiddenSelectionPolling();
            }
        }, 500);
    };

    const afterModalOpen = (mode) => {
        if (mode === MODES.orlen) {
            bindOrlenWidget();
            scheduleOrlenWidgetEnhancement();
            startOrlenHiddenSelectionPolling();
            return;
        }

        window.dispatchEvent(new Event('resize'));
    };

    const openModal = (opener, mode = state.mode) => {
        const modalId = getModalIdForMode(mode);
        const modal = modalId ? document.getElementById(modalId) : null;
        if (!modal?.show) return;

        if (scriptController?.executeWhenScriptLoaded) {
            scriptController.executeWhenScriptLoaded(['modal-dialog.js'], () => {
                modal.show(opener);
                afterModalOpen(mode);
            });
            return;
        }

        modal.show(opener);
        afterModalOpen(mode);
    };

    const openFromDrawer = (drawer, opener) => {
        let opened = false;
        let timeoutId = null;

        const openOnce = () => {
            if (opened) return;
            opened = true;
            if (timeoutId) window.clearTimeout(timeoutId);
            openModal(opener, state.mode);
        };

        const markDrawerForReopen = () => {
            state.reopenDrawer = drawer;
            state.reopenDrawerOpener = opener;
        };

        const onDrawerClosed = () => {
            markDrawerForReopen();
            openOnce();
        };

        drawer.addEventListener('modalClosed', onDrawerClosed, { once: true });

        timeoutId = window.setTimeout(() => {
            drawer.removeEventListener('modalClosed', onDrawerClosed);
            if (!drawer.hasAttribute('open')) {
                markDrawerForReopen();
            }
            openOnce();
        }, 450);

        try {
            drawer.hide(() => {
                drawer.removeEventListener('modalClosed', onDrawerClosed);
                markDrawerForReopen();
                openOnce();
            });
        } catch (error) {
            drawer.removeEventListener('modalClosed', onDrawerClosed);
            log('Drawer hide failed, opening modal directly', error);
            openOnce();
        }
    };

    const handleOpenClick = (event, opener = event.currentTarget) => {
        if (!(opener instanceof Element)) return;
        event.preventDefault();

        const carrier = getCarrierForMode(state.mode);
        const carrierSettings = getCarrierSettings(carrier);

        if (!carrierSettings?.token) {
            const container = opener.closest(SELECTORS.container);
            const message = getCarrierDatasetValue(container, 'MissingToken', carrier);
            if (message) showError(message);
            return;
        }

        state.lastContextKey = getContextKeyFromElement(opener);

        const drawer = opener.closest('cart-drawer');
        if (drawer?.hide && drawer.hasAttribute('open')) {
            openFromDrawer(drawer, opener);
        } else {
            openModal(opener, state.mode);
        }
    };

    const bindContainers = () => {
        getContainers().forEach((container) => {
            if (container.dataset.parcelLockerBound) return;
            container.dataset.parcelLockerBound = 'true';
        });
    };

    const bindDelegatedEvents = () => {
        if (document.body.dataset.parcelLockerEventsBound) return;
        document.body.dataset.parcelLockerEventsBound = 'true';

        document.addEventListener('click', (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const openButton = target.closest(SELECTORS.open);
            if (!openButton) return;

            handleOpenClick(event, openButton);
        });

        document.addEventListener('change', (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (!target.matches(SELECTORS.modeInputs)) return;

            handleModeChange(event);
        });
    };

    const bindCheckoutForms = () => {
        const forms = [document.getElementById('cart'), document.getElementById('cartDrawerForm')];
        forms.forEach((form) => {
            if (!form || form.dataset.parcelLockerBound) return;
            form.dataset.parcelLockerBound = 'true';

            form.addEventListener('submit', (event) => {
                if (!shouldRequireSelection()) return;
                if (state.currentId) return;

                event.preventDefault();
                showError(null, form);
                scrollToWidget(form);
            });
        });
    };

    const bindGlobalCheckoutGuard = () => {
        if (document.body.dataset.parcelLockerCheckoutGuardBound) return;
        document.body.dataset.parcelLockerCheckoutGuardBound = 'true';

        document.addEventListener(
            'click',
            (event) => {
                const target = event.target;
                if (!(target instanceof Element)) return;

                const checkoutControl = target.closest(SELECTORS.checkoutControls);
                if (checkoutControl) {
                    blockCheckoutWithoutSelection(event, checkoutControl);
                    return;
                }

                const checkoutLink = target.closest(SELECTORS.checkoutLinks);
                if (!checkoutLink) return;

                const href = checkoutLink.getAttribute('href') || '';
                if (!/\/checkout(\b|[/?#])/i.test(href)) return;

                blockCheckoutWithoutSelection(event, checkoutLink);
            },
            true
        );

        document.addEventListener(
            'submit',
            (event) => {
                const form = event.target;
                if (!(form instanceof HTMLFormElement)) return;

                const submitter = event.submitter instanceof Element ? event.submitter : null;
                const isCheckoutSubmit =
                    Boolean(submitter?.matches(SELECTORS.checkoutControls)) ||
                    Boolean(form.querySelector(SELECTORS.checkoutControls));
                if (!isCheckoutSubmit) return;

                blockCheckoutWithoutSelection(event, submitter || form);
            },
            true
        );

        document.addEventListener('krem:parcel-locker-selection-required', (event) => {
            const contextKey = event.detail?.context || '';
            const container = contextKey
                ? document.querySelector(`${SELECTORS.container}[data-parcel-locker-context="${contextKey}"]`)
                : null;
            showMissingSelection(container || document.querySelector(SELECTORS.container));
        });
    };

    const bindCheckoutLinks = () => {
        if (document.body.dataset.parcelLockerCheckoutLinksBound) return;
        document.body.dataset.parcelLockerCheckoutLinksBound = 'true';

        document.addEventListener(
            'click',
            (event) => {
                const target = event.target;
                if (!(target instanceof Element)) return;

                const checkoutLink = target.closest(SELECTORS.checkoutLinks);
                if (!checkoutLink) return;

                const href = checkoutLink.getAttribute('href') || '';
                if (!/\/checkout(\b|[/?#])/i.test(href)) return;

                const scope = checkoutLink.closest(
                    '#MainCartFooter, #CartDrawerSection, #cart, #cartDrawerForm, cart-drawer'
                );
                if (!scope) return;
                if (!shouldRequireSelection() || state.currentId) return;

                event.preventDefault();
                showError(null, scope);
                scrollToWidget(scope);
            },
            true
        );
    };

    const bindModalEvents = () => {
        Object.values(MODAL_IDS).forEach((modalId) => {
            const modal = document.getElementById(modalId);
            if (!modal || modal.dataset.parcelLockerBound) return;

            modal.dataset.parcelLockerBound = 'true';
            modal.addEventListener('modalClosed', () => {
                stopOrlenHiddenSelectionPolling();
                if (!state.reopenDrawer) return;
                state.reopenDrawer.show(state.reopenDrawerOpener);
                state.reopenDrawer = null;
                state.reopenDrawerOpener = null;
            });
        });
    };

    const init = () => {
        state.settings = getSettings();
        if (!state.settings?.enabled) return;

        bindWidget();
        bindContainers();
        bindDelegatedEvents();
        bindCheckoutForms();
        bindGlobalCheckoutGuard();
        bindCheckoutLinks();
        bindModalEvents();
        refreshFromCart();

        if (window.Shopify?.designMode) {
            const container = getContainers()[0];
            const missingCarrier = Object.keys(state.settings.carriers).find((carrier) => {
                const settings = state.settings.carriers[carrier];
                return settings.enabled && !settings.token;
            });
            if (missingCarrier) {
                const message = getCarrierDatasetValue(container, 'MissingToken', missingCarrier);
                if (message) showError(message);
            }
        }

        if (typeof subscribe === 'function') {
            subscribe(PUB_SUB_EVENTS.cartUpdateFinish, () => {
                bindContainers();
                bindCheckoutForms();
                refreshFromCart();
            });
        }
    };

    requestAnimationFrame(() => {
        themeBase.onDOMContentLoadedTrigger(() => {
            init();
        });
    });
})();
